import type { CSSProperties } from 'react';
import { apiClient } from './api';

/**
 * Homepage copy staff can edit for themselves (issue #132), extended with the
 * hero background and the two sections below the hero (issue #140).
 *
 * The defaults mirror the backend's, on purpose: the homepage renders them
 * immediately and only repaints if the fetched content differs, so a slow or
 * failed request costs nothing a customer would notice.
 */
export interface HomepageFeatureCard {
  icon: string;
  title: string;
  text: string;
}

export interface HomepageContent {
  heroHeadline: string;
  heroSubtext: string;
  /** 'PhotoWeb/<file>' when an admin has uploaded one; '' = built-in artwork. */
  heroImage: string;
  /** '#RRGGBB', or '' for the built-in green gradient. */
  heroBgColor: string;
  /** 'PhotoWeb/<file>' for a photo behind the hero; '' for none. */
  heroBgImage: string;
  /** The background photo's focus point, 'X% Y%' (issue #154). */
  heroBgPosition: string;
  /** How far the photo is zoomed in, in percent from 100 to 200 (issue #162). */
  heroBgZoom: number;
  promoEnabled: boolean;
  promoText: string;
  featuresTitle: string;
  featuresSubtitle: string;
  /** '#RRGGBB', or '' for the built-in white. */
  featuresBgColor: string;
  features: HomepageFeatureCard[];
  ctaTitle: string;
  ctaText: string;
  ctaButton: string;
  /** '#RRGGBB', or '' for the built-in grey. */
  ctaBgColor: string;
}

export const DEFAULT_HOMEPAGE_CONTENT: HomepageContent = {
  heroHeadline: 'Importer and Wholesaler of all egg products',
  heroSubtext:
    'Welcome to Dasoon official site. We are your trusted partner for premium quality eggs and egg products, serving businesses and consumers with excellence.',
  heroImage: '',
  heroBgColor: '',
  heroBgImage: '',
  heroBgPosition: '50% 50%',
  heroBgZoom: 100,
  promoEnabled: false,
  promoText: '',
  featuresTitle: 'Why Choose Dasoon?',
  featuresSubtitle: "We're committed to delivering the finest quality eggs with exceptional service",
  featuresBgColor: '',
  features: [
    {
      icon: '🥚',
      title: 'Premium Quality',
      text: 'Sourced from certified farms and suppliers, our eggs meet the highest quality standards for freshness and nutrition.',
    },
    {
      icon: '🏆',
      title: 'Halal Certified',
      text: 'All our products are Halal certified, ensuring they meet religious requirements and quality standards.',
    },
    {
      icon: '🚚',
      title: 'Wholesale & Retail',
      text: 'We serve both wholesale businesses and individual customers with flexible ordering and competitive pricing.',
    },
  ],
  ctaTitle: 'Ready to Order?',
  ctaText:
    'Browse our complete selection of premium egg products and place your order today for the freshest quality.',
  ctaButton: 'View Products',
  ctaBgColor: '',
};

/** The built-in hero artwork, used until an admin uploads their own. */
export const BUILT_IN_HERO_IMAGE = '/images/dasoon-hero-egg.png';

export const heroImageUrl = (heroImage: string): string =>
  heroImage ? `/api/v1/${heroImage}` : BUILT_IN_HERO_IMAGE;

/** URL for any uploaded PhotoWeb path, e.g. the hero background photo. */
export const photoWebUrl = (path: string): string => `/api/v1/${path}`;

/**
 * The hero's photo background: darkened so the white headline stays readable,
 * and positioned on the focus point staff chose (issue #154). With
 * `background-size: cover`, the photo's X% Y% point sits at the section's
 * X% Y% point, so it stays in view on a wide screen and a tall phone alike.
 * Shared by the homepage and the admin preview so the two never disagree.
 */
export const heroPhotoBackground = (imageUrl: string, position: string): string =>
  `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url("${imageUrl}") ${position} / cover no-repeat`;

/**
 * The hero photo as its own layer filling the section, zoomed in by `zoom`
 * percent (issue #162). With `cover` a photo fits the section exactly one way,
 * so on its own the focus point can only move it the other way; zooming leaves
 * room both ways. It scales about the focus point, so that point stays put.
 * The section must be `position: relative; overflow: hidden`, with its content
 * stacked above this layer.
 */
export const heroPhotoLayerStyle = (imageUrl: string, position: string, zoom: number): CSSProperties => ({
  position: 'absolute',
  inset: 0,
  background: heroPhotoBackground(imageUrl, position),
  transform: zoom > 100 ? `scale(${zoom / 100})` : undefined,
  transformOrigin: position,
});

/**
 * Whether a '#RRGGBB' background is dark enough to need light text. Staff can
 * pick any colour for a section, so its text colour follows the background
 * rather than staying dark on a navy section (issue #140).
 */
export const isDarkColor = (hex: string): boolean => {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!match) return false;
  const [r, g, b] = match.slice(1).map((part) => parseInt(part, 16) / 255);
  const linear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * linear(r!) + 0.7152 * linear(g!) + 0.0722 * linear(b!);
  // 0.179 is where black and white text give equal contrast (WCAG).
  return luminance < 0.179;
};

export const homepageService = {
  async getContent(): Promise<HomepageContent> {
    const response = await apiClient.get<HomepageContent>('/content/homepage');
    // Merged over the defaults so a backend that predates a field still
    // renders the shipped copy for it.
    return { ...DEFAULT_HOMEPAGE_CONTENT, ...(response.data ?? {}) };
  },
};
